/**
 * ForgotPassword
 *
 * Sends a Firebase password reset email and provides clear success/error UI
 * with a minimal landing header and dark-mode support.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { auth } from '../../../firebase/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import useDarkMode from '../../../hooks/useDarkMode';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import Footer from '../../../components/shared/Footer';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const isDark = useDarkMode();

  const toggleTheme = () => {
    // This will be handled by the useDarkMode hook
  };

  const handleNavigation = () => {
    // Handle any navigation logic if needed
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/auth-action`,
        handleCodeInApp: false,
      };
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setSuccessMessage('Password reset email sent! Check your inbox.');
      // Don't clear the email so the user can see what address they used
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setError(err.message.includes('Firebase') 
        ? 'No account found with this email address.' 
        : err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Use the SimpleLandingHeader component */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/signin"
        backButtonText="Back to sign in"
        onNavigation={handleNavigation}
        showLogo={true}
      />
      
      {/* Content section */}
      <div className="bg-white dark:bg-gray-900 px-4 py-12 w-full">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
          <div className="text-center">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400">Forgot Password</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight max-w-[90%] md:max-w-full mx-auto">
              Reset your password
            </p>
            <p className="mt-4 text-lg text-gray-700 dark:text-gray-300 max-w-[90%] md:max-w-full mx-auto">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px] rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px] rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700/50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-200">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] px-6 py-8 mt-6 sm:px-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white"
              >
                Email address
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`flex w-full justify-center items-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-3.5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 ${isLoading ? 'opacity-95 cursor-not-allowed' : ''}`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span>Sending...</span>
                  </>
                ) : (
                  'Send reset link'
                )}
              </button>
            </div>

            <div className="text-center">
              <Link 
                to="/signin" 
                className="text-sm font-semibold text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
              >
                Back to sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
      
      <Footer />
    </div>
  );
} 